import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Check, Clock, ShieldCheck, TriangleAlert, X} from 'lucide-react';
import {Button, Progress, cn} from '@chekinapp/ui';
import {formatMoney} from '../../money';
import type {
  PaymentFailureReason,
  TimelineRow,
  TimelineRowState,
} from '../../paymentTimeline';

type ProgressPhase = 'processing' | 'success' | 'error';

const BOX_TONE: Record<TimelineRowState, string> = {
  wait: 'bg-[var(--chekin-neutral-50)] opacity-50',
  on: 'bg-[color-mix(in_srgb,var(--chekin-color-brand-blue)_8%,var(--chekin-color-white))]',
  done: 'bg-[var(--chekin-green-50)]',
  fail: 'bg-[var(--chekin-neutral-50)]',
  void: 'bg-[var(--chekin-neutral-50)]',
};

const MARK_TONE: Record<TimelineRowState, string> = {
  wait: 'bg-[var(--chekin-neutral-100)] text-[var(--chekin-text-tertiary)]',
  on: 'animate-spin [animation-duration:1.5s] border-[3px] border-[color-mix(in_srgb,var(--chekin-color-brand-blue)_14%,var(--chekin-color-white))] border-t-[var(--chekin-color-brand-blue)] motion-reduce:[animation-duration:3s]',
  done: 'bg-[var(--chekin-green-600)] text-[var(--chekin-color-white)]',
  fail: 'bg-[var(--chekin-warning)] text-[var(--chekin-color-white)]',
  void: 'bg-[var(--chekin-neutral-300)] text-[var(--chekin-color-white)]',
};

const MARK_ICON: Record<TimelineRowState, ReactNode> = {
  wait: <Clock className="size-3.5" />,
  on: null,
  done: <Check strokeWidth={3} className="size-3.5" />,
  fail: <TriangleAlert strokeWidth={2.5} className="size-3.5" />,
  void: <X strokeWidth={3} className="size-3.5" />,
};

function StepMark({state}: {state: TimelineRowState}) {
  return (
    <span
      aria-hidden
      className={cn(
        'mt-px grid size-6 shrink-0 place-items-center rounded-full transition-colors duration-300',
        MARK_TONE[state],
      )}
    >
      {MARK_ICON[state]}
    </span>
  );
}

const ACTIVE_WEIGHT: Record<TimelineRow['key'], number> = {
  bank: 0.3,
  hold: 0.82,
  charge: 0.82,
};

const BAR_TONE: Record<ProgressPhase, string> = {
  processing: '',
  success:
    '[--progress-indicator-from:var(--chekin-green-700)] [--progress-indicator-to:var(--chekin-green-700)]',
  error:
    '[--progress-indicator-from:var(--chekin-color-brand-red)] [--progress-indicator-to:var(--chekin-color-brand-red)]',
};

function progressPercent(rows: TimelineRow[], phase: ProgressPhase) {
  if (phase !== 'processing' || rows.length === 0) return 100;
  const done = rows.filter(row => row.state === 'done').length;
  const active = rows.find(row => row.state === 'on');
  return ((done + (active ? ACTIVE_WEIGHT[active.key] : 0)) / rows.length) * 100;
}

type RowVariant = 'bank' | 'hold' | 'authorise' | 'charge';

type CopyKeys = {title: string; description: string};

const ROW_COPY: Record<RowVariant, Partial<Record<TimelineRowState, CopyKeys>>> = {
  bank: {
    on: {
      title: 'pay_flow.row.bank.on.title',
      description: 'pay_flow.row.bank.on.description',
    },
    done: {
      title: 'pay_flow.row.bank.done.title',
      description: 'pay_flow.row.bank.done.description',
    },
    fail: {
      title: 'pay_flow.row.bank.fail.title',
      description: 'pay_flow.row.bank.fail.description',
    },
    void: {
      title: 'pay_flow.row.bank.void.title',
      description: 'pay_flow.row.bank.void.description',
    },
  },
  hold: {
    wait: {
      title: 'pay_flow.row.hold.wait.title',
      description: 'pay_flow.row.hold.wait.description',
    },
    on: {
      title: 'pay_flow.row.hold.on.title',
      description: 'pay_flow.row.hold.on.description',
    },
    done: {
      title: 'pay_flow.row.hold.done.title',
      description: 'pay_flow.row.hold.done.description',
    },
    fail: {
      title: 'pay_flow.row.hold.fail.title',
      description: 'pay_flow.row.hold.fail.description',
    },
    void: {
      title: 'pay_flow.row.hold.void.title',
      description: 'pay_flow.row.hold.void.description',
    },
  },
  authorise: {
    wait: {
      title: 'pay_flow.row.authorise.wait.title',
      description: 'pay_flow.row.authorise.wait.description',
    },
    on: {
      title: 'pay_flow.row.authorise.on.title',
      description: 'pay_flow.row.authorise.on.description',
    },
    done: {
      title: 'pay_flow.row.authorise.done.title',
      description: 'pay_flow.row.authorise.done.description',
    },
    fail: {
      title: 'pay_flow.row.authorise.fail.title',
      description: 'pay_flow.row.authorise.fail.description',
    },
    void: {
      title: 'pay_flow.row.authorise.void.title',
      description: 'pay_flow.row.authorise.void.description',
    },
  },
  charge: {
    on: {
      title: 'pay_flow.row.charge.on.title',
      description: 'pay_flow.row.charge.on.description',
    },
    done: {
      title: 'pay_flow.row.charge.done.title',
      description: 'pay_flow.row.charge.done.description',
    },
    fail: {
      title: 'pay_flow.row.charge.fail.title',
      description: 'pay_flow.row.charge.fail.description',
    },
    void: {
      title: 'pay_flow.row.charge.void.title',
      description: 'pay_flow.row.charge.void.description',
    },
    wait: {
      title: 'pay_flow.row.charge.wait.title',
      description: 'pay_flow.row.charge.wait.description',
    },
  },
};

const REASON_COPY: Record<
  PaymentFailureReason,
  {title: string; body: string; hint: string}
> = {
  declined: {
    title: 'pay_flow.reason.declined.title',
    body: 'pay_flow.reason.declined.body',
    hint: 'pay_flow.reason.declined.hint',
  },
  funds: {
    title: 'pay_flow.reason.funds.title',
    body: 'pay_flow.reason.funds.body',
    hint: 'pay_flow.reason.funds.hint',
  },
  expired: {
    title: 'pay_flow.reason.expired.title',
    body: 'pay_flow.reason.expired.body',
    hint: 'pay_flow.reason.expired.hint',
  },
  cvc: {
    title: 'pay_flow.reason.cvc.title',
    body: 'pay_flow.reason.cvc.body',
    hint: 'pay_flow.reason.cvc.hint',
  },
  authentication: {
    title: 'pay_flow.reason.authentication.title',
    body: 'pay_flow.reason.authentication.body',
    hint: 'pay_flow.reason.authentication.hint',
  },
  hold: {
    title: 'pay_flow.reason.hold.title',
    body: 'pay_flow.reason.hold.body',
    hint: 'pay_flow.reason.hold.hint',
  },
  generic: {
    title: 'pay_flow.reason.generic.title',
    body: 'pay_flow.reason.generic.body',
    hint: 'pay_flow.reason.generic.hint',
  },
};

function rowCopy(row: TimelineRow) {
  const variant = row.key === 'hold' && row.isAuthorisation ? 'authorise' : row.key;
  return ROW_COPY[variant][row.state];
}

type BodyProps = {
  phase: ProgressPhase;
  rows: TimelineRow[];
  isBankConfirming: boolean;
  reason?: PaymentFailureReason;
  amountLabel: string;
  currencySymbol: string;
};

function PaymentProgressBody({
  phase,
  rows,
  isBankConfirming,
  reason = 'generic',
  amountLabel,
  currencySymbol,
}: BodyProps) {
  const {t} = useTranslation();
  const hasHold = rows.some(row => row.key === 'hold');
  const operations = rows.filter(row => row.key !== 'bank').length;
  const isBankActive = rows.some(row => row.key === 'bank' && row.state === 'on');
  const nothingTaken =
    phase === 'error' && !rows.some(row => row.key !== 'bank' && row.state === 'done');

  const [title, subtitle] =
    phase === 'success'
      ? [
          t('pay_flow.done_title'),
          t(operations > 1 ? 'pay_flow.done_two' : 'pay_flow.done_one'),
        ]
      : phase === 'error'
      ? [
          t('pay_flow.failed_title'),
          `${t(REASON_COPY[reason].title)}. ${t(REASON_COPY[reason].body)}`,
        ]
      : isBankActive
      ? [t('pay_flow.bank_title'), t('pay_flow.bank_subtitle', {amount: amountLabel})]
      : [t('pay_flow.processing_title'), t('pay_flow.processing_subtitle')];

  const items = rows.map(row => {
    const copy = rowCopy(row);
    return {
      key: row.key,
      state: row.state,
      title: copy ? t(copy.title) : '',
      description:
        row.key === 'charge' && row.state === 'wait' && hasHold
          ? t('pay_flow.row.charge.wait.after_hold')
          : copy
          ? t(copy.description)
          : '',
      amount: row.amount ? formatMoney(row.amount, currencySymbol) : undefined,
      note:
        row.key === 'bank' && row.state === 'on' && isBankConfirming
          ? t('pay_flow.zero_amount_note', {amount: formatMoney(0, currencySymbol)})
          : undefined,
    };
  });

  return (
    <div className="flex flex-col gap-3" aria-live="polite">
      <Progress
        value={progressPercent(rows, phase)}
        className={cn('[--progress-height:4px]', BAR_TONE[phase])}
        aria-label={title}
      />
      <div>
        <h2 className="text-[20px] font-semibold leading-[26px] tracking-[-0.01em] text-[var(--chekin-color-brand-navy)]">
          {title}
        </h2>
        <p className="mt-1 text-[14px] leading-5 text-[var(--chekin-text-secondary)]">
          {subtitle}
        </p>
      </div>

      <ol className="mt-0.5 flex flex-col gap-[18px]">
        {items.map((item, index) => (
          <li
            key={item.key}
            data-state={item.state}
            className={cn(
              'relative flex items-start gap-3 rounded-[14px] px-3.5 py-3 transition-[background-color,opacity] duration-[450ms] animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none',
              BOX_TONE[item.state],
              item.key === 'hold' &&
                cn(
                  'outline-dashed outline-[1.5px] -outline-offset-[1.5px]',
                  item.state === 'done'
                    ? 'outline-[color:var(--chekin-green-600)]'
                    : 'outline-[color:var(--chekin-blue-200)]',
                ),
            )}
          >
            {index < items.length - 1 && (
              <span
                aria-hidden
                className="absolute left-[25px] top-full h-[18px] w-0.5 overflow-hidden rounded-full bg-[var(--chekin-neutral-200)]"
              >
                <span
                  className={cn(
                    'block size-full origin-top bg-[var(--chekin-green-600)] transition-transform duration-500 ease-[cubic-bezier(.3,.7,.2,1)] motion-reduce:transition-none',
                    item.state === 'done' ? 'scale-y-100' : 'scale-y-0',
                  )}
                />
              </span>
            )}
            <StepMark state={item.state} />
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-semibold leading-5 text-[var(--chekin-color-brand-navy)]">
                {item.title}
              </p>
              <p className="text-[13px] leading-[18px] text-[var(--chekin-text-secondary)]">
                {item.description}
              </p>
              {item.note && (
                <p className="mt-2 text-[12px] italic leading-[17px] text-[var(--chekin-text-secondary)]">
                  {item.note}
                </p>
              )}
            </div>
            {item.amount && (
              <em
                className={cn(
                  'shrink-0 text-[14px] font-bold not-italic tabular-nums text-[var(--chekin-color-brand-navy)]',
                  (item.state === 'void' || item.state === 'fail') &&
                    'text-[var(--chekin-text-tertiary)] line-through',
                )}
              >
                {item.amount}
              </em>
            )}
          </li>
        ))}
      </ol>

      {nothingTaken && (
        <p className="flex items-start gap-2 text-[13px] leading-[18px] text-[var(--chekin-text-secondary)]">
          <ShieldCheck className="mt-px size-4 shrink-0 text-[var(--chekin-green-700)]" />
          <span>
            <b className="font-semibold text-[var(--chekin-color-brand-navy)]">
              {t(hasHold ? 'pay_flow.nothing_taken_with_hold' : 'pay_flow.nothing_taken')}
            </b>{' '}
            {t(REASON_COPY[reason].hint)}
          </span>
        </p>
      )}
    </div>
  );
}

type ActionsProps = {
  phase: ProgressPhase;
  onTryAgain: () => void;
  onUseAnotherCard: () => void;
  successActions?: ReactNode;
};

function PaymentProgressActions({
  phase,
  onTryAgain,
  onUseAnotherCard,
  successActions,
}: ActionsProps) {
  const {t} = useTranslation();

  if (phase === 'error') {
    return (
      <>
        <Button onClick={onTryAgain} className="w-full">
          {t('try_again')}
        </Button>
        <Button variant="ghost" onClick={onUseAnotherCard} className="w-full">
          {t('pay_flow.use_another_card')}
        </Button>
      </>
    );
  }

  if (phase === 'success' && successActions) return <>{successActions}</>;

  return null;
}

export {PaymentProgressActions, PaymentProgressBody};
export type {ProgressPhase};
